// 生成工作台（027 P2）· 四栏布局 + job_messages 真实叙事流 + 产物预览
// 视觉基准：UI_design_2/workspace.html（唯一权威）；消息真实落库（刷新不丢），
// Realtime INSERT 推送；历史任务无消息时回退 P1 步骤合成叙事；P3 接入暂停/打断协议。
import { Component, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type ProjectListItem } from "../lib/api";
import { supabase } from "../lib/supabase";
import { V2_BG_LABEL, V2_LAYOUT_NAMES } from "../lib/slides";
import { KBS_STEPS } from "../lib/kbs-registry.generated";
import { useAuth } from "../auth";
import { BrandLogo } from "../components/BrandLogo";
import "./Workspace.css";

type JobInfo = Awaited<ReturnType<typeof api.getJob>>;
type StepInfo = JobInfo["steps"][number];

/** job_messages 行（worker 真实落库的叙事消息） */
interface WsMessage {
  id: string;
  seq: number;
  role: "agent" | "user" | "system";
  stage: string | null;
  agent_name: string | null;
  think: string | null;
  say: string;
  artifact_ref: string | null;
  created_at: string;
}

/** research 的 competitors 兼容两种形状：数组 或 {landscape, competitors:[…]} */
type CompetitorItem = { name?: string; positioning?: string };
function pickCompetitors(o: Record<string, unknown>): { items: CompetitorItem[]; landscape: string | null } {
  const raw = o.competitors;
  if (Array.isArray(raw)) return { items: raw as CompetitorItem[], landscape: null };
  if (raw && typeof raw === "object") {
    const inner = (raw as { competitors?: unknown }).competitors;
    const landscape = (raw as { landscape?: unknown }).landscape;
    return {
      items: Array.isArray(inner) ? (inner as CompetitorItem[]) : [],
      landscape: typeof landscape === "string" ? landscape : null,
    };
  }
  return { items: [], landscape: null };
}

/** review 的 comments 兼容字符串或 {page, issue, suggestion} 对象 */
function commentText(c: unknown): string {
  if (typeof c === "string") return c;
  if (c && typeof c === "object") {
    const o = c as { page?: number; issue?: string; suggestion?: string };
    return `第${o.page ?? "—"}页：${o.issue ?? ""}${o.suggestion ? `（建议：${o.suggestion}）` : ""}`;
  }
  return String(c ?? "");
}

/** 049 F10：artwork 失败记录的 [分类] 前缀 → 用户可读原因（无分类/无记录时给兜底文案） */
function artworkFailReason(err?: string): string {
  const m = err?.match(/^\[(\w+)\]/);
  const map: Record<string, string> = {
    http_5xx: "图像服务网关繁忙（HTTP 5xx，供应商侧未创建任务）",
    http_429: "图像服务限流（HTTP 429）",
    http_4xx: "图像请求参数/鉴权异常（HTTP 4xx）",
    network: "图像请求未发出（网络/TLS 中断）",
    timeout: "图像生成超时",
    no_url: "图像服务响应异常（未返回图片地址）",
  };
  return (m && map[m[1]]) || "图像服务暂时不可用";
}

/** 防白屏：预览渲染遇到意外数据形状时降级提示，不拖垮整个工作台 */
class WsErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) return <p style={{ color: "var(--ws-muted)" }}>该阶段产物预览渲染异常，不影响生成流程。</p>;
    return this.props.children;
  }
}

/** 阶段元数据（栏 4 产物列表 + 流分隔条共用）：两套流水线各自一张表，按 job.pipeline 选 */
interface StageUi { key: string; label: string; icon: string; divider: string }

const LEGACY_STAGES: StageUi[] = [
  { key: "intake", label: "资料解析", icon: "📄", divider: "读懂品牌 · 资料解析" },
  { key: "research", label: "市场调研", icon: "🔍", divider: "寻找机会 · 行业研究" },
  { key: "strategy", label: "策略规划", icon: "🎯", divider: "形成策略 · 定位与 Big Idea" },
  { key: "financial", label: "经营测算", icon: "📊", divider: "算清账 · 经营测算" },
  { key: "outline", label: "PPT 大纲", icon: "📋", divider: "搭结构 · PPT 大纲" },
  { key: "drafting", label: "文案撰写", icon: "✍️", divider: "写文案 · 逐页撰写" },
  { key: "review", label: "评审打磨", icon: "🔎", divider: "评审打磨" },
  { key: "assemble", label: "装配出片", icon: "🎬", divider: "装配出片" },
];

/** KBS 19 步：label/icon 从后端注册表派生（032 §2 禁硬编码），divider 为展示文案 */
const KBS_DIVIDERS: Record<string, string> = {
  intake: "读懂品牌 · 资料解析",
  k1: "K 线 · 品类地图扫描", k2: "K 线 · 消费者洞察", k3: "K 线 · 机会空间定义", k4: "K 线 · 洞察报告汇总",
  b1: "B 线 · 定位罗盘", b2: "B 线 · 价值金字塔", b3: "B 线 · 品牌故事", b4: "B 线 · 品牌架构", b5: "B 线 · 蓝皮书汇总",
  s1: "S 线 · Big Idea 创意", s2: "S 线 · 传播框架", s3: "S 线 · 内容矩阵", s4: "S 线 · 创意简报",
  financial: "算清账 · 经营测算（附录）", outline: "搭结构 · PPT 大纲", drafting: "写文案 · 逐页撰写",
  review: "评审打磨", assemble: "装配出片",
};
const KBS_STAGES_UI: StageUi[] = KBS_STEPS.map((s) => ({
  key: s.key, label: s.label, icon: s.icon, divider: KBS_DIVIDERS[s.key] ?? s.label,
}));

function stagesFor(pipeline?: string): StageUi[] {
  return pipeline === "legacy" ? LEGACY_STAGES : KBS_STAGES_UI;
}

/** 栏 2 品牌资料字段映射（projects.form → 中文标签） */
const FORM_LABELS: Record<string, string> = {
  product: "核心产品", audience: "目标人群", goal: "营销目标",
  budget: "预算", style: "视觉风格", theme_id: "视觉主题",
};

/** 从 step output 提取人类可读摘要（复用旧 Job 页逻辑，作为叙事 think 块） */
function summarize(step: StepInfo): string | null {
  const o = step.output;
  if (!o) return null;
  switch (step.step) {
    case "intake": {
      const brand = (o as Record<string, unknown>).brand ?? (o as Record<string, unknown>).oneLiner;
      const products = (o as { products?: unknown[] }).products;
      if (brand) return `品牌：${brand}${products?.length ? `，${products.length} 个产品线` : ""}`;
      return ((o as Record<string, unknown>).oneLiner as string) ?? null;
    }
    case "research": {
      const m = (o as { market?: { summary?: string } }).market;
      const { items } = pickCompetitors(o as Record<string, unknown>);
      const parts: string[] = [];
      if (m?.summary) parts.push(m.summary.slice(0, 80));
      if (items.length) parts.push(`${items.length} 个竞品分析`);
      return parts.join("；") || null;
    }
    // ---------- KBS K 线 ----------
    case "k1": {
      const direct = (o as { layerFindings?: { direct?: unknown[] } }).layerFindings?.direct;
      const gap = (o as { map?: { gap?: { label?: string; opportunity?: string } } }).map?.gap;
      const parts: string[] = [];
      if (Array.isArray(direct)) parts.push(`${direct.length} 个直接竞品`);
      if (gap?.label) parts.push(`空白区「${gap.label}」`);
      return parts.join("，") || null;
    }
    case "k2": {
      const personas = (o as { personas?: { name?: string; layers?: { desire?: string } }[] }).personas;
      if (!Array.isArray(personas) || !personas.length) return null;
      return `${personas.length} 张人群卡：${personas.map((p) => p.name ?? "—").join("、")}`;
    }
    case "k3": {
      const ns = (o as { northStar?: { sentence?: string } }).northStar?.sentence;
      return ns ? `北极星：${ns.slice(0, 80)}` : null;
    }
    case "k4": {
      const es = (o as { executiveSummary?: string }).executiveSummary;
      return es ? es.slice(0, 100) : null;
    }
    // ---------- KBS B 线 ----------
    case "b1": {
      const p = (o as { promise?: { sentence?: string } }).promise?.sentence;
      return p ? `品牌承诺：${p.slice(0, 80)}` : null;
    }
    case "b2": {
      const core = (o as { pyramid?: { corePromise?: string } }).pyramid?.corePromise;
      return core ? `核心承诺：${core}` : null;
    }
    case "b3": {
      const t = (o as { storyText?: string }).storyText;
      return t ? `品牌故事 ${t.length} 字：${t.slice(0, 60)}…` : null;
    }
    case "b4": {
      const app = (o as { applicable?: boolean }).applicable;
      if (app === false) return "单品牌单业务，维持单一架构";
      const rec = (o as { decision?: { recommendedLabel?: string } }).decision?.recommendedLabel;
      return rec ? `推荐架构：${rec}` : null;
    }
    case "b5": {
      const kpis = (o as { kpis?: unknown[] }).kpis;
      return kpis ? `蓝皮书收束：语言规范 + 优先级 + ${kpis.length} 项 KPI` : null;
    }
    // ---------- KBS S 线 ----------
    case "s1": {
      const phrase = (o as { bigIdea?: { phrase?: string } }).bigIdea?.phrase;
      return phrase ? `Big Idea：「${phrase}」` : null;
    }
    case "s2": {
      const phases = (o as { phases?: unknown[] }).phases;
      return phases ? `${phases.length} 阶段传播节奏 + 四渠道矩阵` : null;
    }
    case "s3": {
      const cells = (o as { cells?: unknown[] }).cells;
      return cells ? `内容矩阵 ${cells.length} 格已规划` : null;
    }
    case "s4": {
      const t = (o as { singleTask?: string }).singleTask;
      return t ? `单一传播任务：${t}` : null;
    }
    case "strategy": {
      const idea = (o as Record<string, unknown>).bigIdeaName ?? (o as Record<string, unknown>).bigIdea;
      const pos = (o as { positioning?: string }).positioning;
      if (idea) return `Big Idea：${idea}${pos ? ` — ${pos.slice(0, 50)}` : ""}`;
      return pos?.slice(0, 100) ?? null;
    }
    case "financial": {
      const roi = (o as Record<string, unknown>).roi;
      const forecast = (o as { forecast?: unknown[] }).forecast;
      const roiStr = typeof roi === "object" && roi !== null
        ? ((roi as Record<string, unknown>).roiRatio ?? (roi as Record<string, unknown>).ratio ?? (roi as Record<string, unknown>).value ?? "—")
        : roi;
      if (roiStr) return `ROI ${roiStr}，${forecast?.length ?? 0} 期预测`;
      return forecast ? `${forecast.length} 期财务预测` : null;
    }
    case "outline": {
      const pages = (o as { pages?: unknown[] }).pages;
      if (!pages?.length) return null;
      const kinds = new Set(pages.map((p: unknown) => (p as { layoutV2?: string }).layoutV2).filter(Boolean));
      return `${pages.length} 页大纲已规划${kinds.size ? ` · ${kinds.size} 种版式` : ""}`;
    }
    case "drafting": {
      const slides = (o as { slides?: unknown[] }).slides;
      return slides ? `${slides.length} 页文案已撰写` : null;
    }
    case "review": {
      const pass = (o as { pass?: boolean }).pass;
      const score = (o as { score?: number }).score;
      return `评审${pass ? "通过" : "需修改"}，得分 ${score ?? "—"}/100`;
    }
    case "assemble":
      return "PPT 已生成，可下载";
    default:
      return null;
  }
}

/** outline 版式节奏表（栏 4 预览用） */
function outlineDetail(step: StepInfo): string[] | null {
  if (step.step !== "outline") return null;
  const pages = (step.output as { pages?: { idx?: number; title?: string; layoutV2?: string; bg?: string }[] })?.pages;
  if (!Array.isArray(pages) || !pages.length) return null;
  return pages.map((p, i) => {
    const name = p.layoutV2 ? (V2_LAYOUT_NAMES[p.layoutV2] ?? p.layoutV2) : null;
    const bg = p.bg ? (V2_BG_LABEL[p.bg] ?? p.bg) : null;
    const meta = [name, bg].filter(Boolean).join(" · ");
    const title = String(p.title ?? "").slice(0, 14);
    return `第${String(p.idx ?? i + 1).padStart(2, "0")}页 ${title}${meta ? ` → ${meta}` : ""}`;
  });
}

function fmtTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return [d.getHours(), d.getMinutes()].map((n) => String(n).padStart(2, "0")).join(":");
}

/** KBS 搜索引用：按 tag 分组展示（第一层·竞品 / 消费者声音 等，034 §1.2） */
function KbsCites({ step }: { step: StepInfo }) {
  const cites = step.citations ?? [];
  if (!cites.length) return null;
  const groups = new Map<string, typeof cites>();
  for (const c of cites) {
    const tag = c.tag ?? "搜索材料";
    const list = groups.get(tag) ?? [];
    list.push(c);
    groups.set(tag, list);
  }
  return (
    <>
      {[...groups.entries()].map(([tag, list]) => (
        <div key={tag} className="aim-ws-fact">
          <b>{tag} · 引用 {list.length}</b>
          {list.slice(0, 4).map((ct, i) => (
            <a key={i} className="aim-ws-cite" href={ct.url} target="_blank" rel="noopener">↗ {ct.title || ct.url}</a>
          ))}
        </div>
      ))}
    </>
  );
}

/* ---------- 栏 4 预览面板：由 step.output 映射（React 自动转义，无注入风险） ---------- */
function PreviewBody({ step, deckId }: { step: StepInfo; deckId: string | null }) {
  const nav = useNavigate();
  const o = (step.output ?? {}) as Record<string, never>;
  switch (step.step) {
    case "intake": {
      const brand = (o as Record<string, unknown>).brand as string | undefined;
      const oneLiner = (o as Record<string, unknown>).oneLiner as string | undefined;
      const rawProducts = (o as Record<string, unknown>).products;
      const products = Array.isArray(rawProducts) ? (rawProducts as { name?: string }[]) : [];
      return (
        <>
          <h3>品牌<em>事实库</em></h3>
          {brand && <div className="aim-ws-fact"><b>品牌</b>{brand}</div>}
          {oneLiner && <div className="aim-ws-fact"><b>一句话</b>{oneLiner}</div>}
          {products.map((p, i) => (
            <div key={i} className="aim-ws-fact"><b>产品线 {i + 1}</b>{p.name ?? "—"}</div>
          ))}
          {!brand && !oneLiner && !products.length && <p>以表单资料为准，未解析出额外事实。</p>}
        </>
      );
    }
    case "research": {
      const m = (o as Record<string, unknown>).market as { summary?: string } | undefined;
      const { items, landscape } = pickCompetitors(o as Record<string, unknown>);
      return (
        <>
          <h3>市场<em>洞察</em></h3>
          {m?.summary && <div className="aim-ws-fact"><b>市场摘要</b>{m.summary}</div>}
          {landscape && <div className="aim-ws-fact"><b>竞争格局</b>{landscape}</div>}
          {items.slice(0, 4).map((cp, i) => (
            <div key={i} className="aim-ws-fact"><b>竞品 {i + 1}</b>{cp.name ?? "—"}{cp.positioning ? ` · ${cp.positioning}` : ""}</div>
          ))}
          {(step.citations?.length ?? 0) > 0 && (
            <div className="aim-ws-fact">
              <b>引用来源（{step.citations.length}，前 5 条）</b>
              {step.citations.slice(0, 5).map((ct, i) => (
                <a key={i} className="aim-ws-cite" href={ct.url} target="_blank" rel="noopener">↗ {ct.title || ct.url}</a>
              ))}
            </div>
          )}
        </>
      );
    }
    case "strategy": {
      const idea = ((o as Record<string, unknown>).bigIdeaName ?? (o as Record<string, unknown>).bigIdea) as string | undefined;
      const pos = (o as Record<string, unknown>).positioning as string | undefined;
      const rawAlt = (o as Record<string, unknown>).alternatives;
      const alt = Array.isArray(rawAlt) ? (rawAlt as { name?: string }[]) : [];
      return (
        <>
          <h3>策略<em>提案</em></h3>
          {idea && <div className="aim-ws-fact"><b>BIG IDEA</b>{idea}</div>}
          {pos && <div className="aim-ws-fact"><b>定位</b>{pos}</div>}
          {alt.slice(0, 3).map((a, i) => (
            <div key={i} className="aim-ws-fact"><b>备选方向 {i + 1}</b>{a.name ?? "—"}</div>
          ))}
        </>
      );
    }
    // ---------- KBS K 线 ----------
    case "k1": {
      const k1 = o as unknown as {
        layerFindings?: { direct?: { name?: string; positioning?: string }[] };
        map?: {
          xAxis?: { neg?: string; pos?: string; rationale?: string };
          yAxis?: { neg?: string; pos?: string };
          gap?: { label?: string; opportunity?: string };
        };
        degraded?: boolean;
      };
      return (
        <>
          <h3>品类<em>地图扫描</em></h3>
          {k1.map?.xAxis && <div className="aim-ws-fact"><b>X 轴</b>{k1.map.xAxis.neg} ←→ {k1.map.xAxis.pos}{k1.map.xAxis.rationale ? `（选轴理由：${k1.map.xAxis.rationale}）` : ""}</div>}
          {k1.map?.yAxis && <div className="aim-ws-fact"><b>Y 轴</b>{k1.map.yAxis.neg} ←→ {k1.map.yAxis.pos}</div>}
          {(k1.layerFindings?.direct ?? []).slice(0, 4).map((c, i) => (
            <div key={i} className="aim-ws-fact"><b>竞品 {i + 1}</b>{c.name ?? "—"}{c.positioning ? ` · ${c.positioning}` : ""}</div>
          ))}
          {k1.map?.gap && <div className="aim-ws-fact"><b>空白区</b>「{k1.map.gap.label}」{k1.map.gap.opportunity ? ` — ${k1.map.gap.opportunity}` : ""}</div>}
          {k1.degraded && <p>⚠ 部分搜索材料缺失，本轮基于事实库推演降级完成。</p>}
          <KbsCites step={step} />
        </>
      );
    }
    case "k2": {
      const k2 = o as unknown as { personas?: { name?: string; quote?: { text?: string; verbatim?: boolean }; portrait?: string; layers?: { behavior?: string; attitude?: string; motivation?: string; barrier?: string; desire?: string }; evidence?: string[] }[] };
      // 048 F6：五层剥离法完整呈现（行为/态度/动机/障碍/渴望 + 证据来源）
      const layerRows: [string, keyof NonNullable<NonNullable<typeof k2.personas>[number]["layers"]>][] = [
        ["行为", "behavior"], ["态度", "attitude"], ["动机", "motivation"], ["障碍", "barrier"], ["渴望", "desire"],
      ];
      return (
        <>
          <h3>消费者<em>真相卡</em></h3>
          {(k2.personas ?? []).map((p, i) => (
            <div key={i} className="aim-ws-fact is-tall">
              <b>{p.name ?? `人群 ${i + 1}`}</b>
              {p.portrait ? `${p.portrait}` : ""}
              {p.quote?.text ? `原话：「${p.quote.text}」${p.quote.verbatim ? "" : "（合成推演）"}` : ""}
              {layerRows.map(([label, key]) => p.layers?.[key] ? (
                <span key={key} className="aim-ws-layer">
                  <i>{label}{p.evidence?.[layerRows.findIndex((r) => r[1] === key)] ? ` · 证据：${p.evidence[layerRows.findIndex((r) => r[1] === key)]}` : ""}</i>
                  {p.layers[key]}
                </span>
              ) : null)}
            </div>
          ))}
          <KbsCites step={step} />
        </>
      );
    }
    case "k3": {
      const k3 = o as unknown as { matrix?: { title?: string; stars?: number }[]; northStar?: { sentence?: string }; entryRationale?: string };
      const top = (k3.matrix ?? []).find((c) => c.stars === 3);
      return (
        <>
          <h3>机会<em>空间定义</em></h3>
          {k3.northStar?.sentence && (
            <div className="aim-ws-northstar"><p className="aim-ws-eyebrow">NORTH STAR</p><p>{k3.northStar.sentence}</p></div>
          )}
          {top && <div className="aim-ws-fact"><b>首选机会格</b>「{top.title}」★★★</div>}
          {k3.entryRationale && <div className="aim-ws-fact"><b>进入理由</b>{k3.entryRationale}</div>}
        </>
      );
    }
    case "k4": {
      const k4 = o as unknown as { executiveSummary?: string; keyFindings?: string[]; nextSteps?: string[] };
      return (
        <>
          <h3>洞察<em>报告汇总</em></h3>
          {k4.executiveSummary && <div className="aim-ws-fact"><b>摘要</b>{k4.executiveSummary}</div>}
          {(k4.keyFindings ?? []).map((f, i) => (
            <div key={i} className="aim-ws-fact"><b>关键发现 {i + 1}</b>{f}</div>
          ))}
          {(k4.nextSteps ?? []).map((n, i) => (
            <div key={i} className="aim-ws-fact"><b>下一步 {i + 1}</b>{n}</div>
          ))}
        </>
      );
    }
    // ---------- KBS B 线 ----------
    case "b1": {
      const b1 = o as unknown as { promise?: { sentence?: string }; audience?: { statement?: string }; trustSignals?: { fact?: string; source?: string }[] };
      return (
        <>
          <h3>定位<em>罗盘</em></h3>
          {b1.promise?.sentence && <div className="aim-ws-fact"><b>品牌承诺</b>{b1.promise.sentence}</div>}
          {b1.audience?.statement && <div className="aim-ws-fact"><b>目标人群</b>{b1.audience.statement}</div>}
          {(b1.trustSignals ?? []).map((t, i) => (
            <div key={i} className="aim-ws-fact"><b>信任状 {i + 1}</b>{t.fact}{t.source ? `（${t.source}）` : ""}</div>
          ))}
        </>
      );
    }
    case "b2": {
      const py = (o as unknown as { pyramid?: { corePromise?: string; reasons?: string[]; evidence?: string[] } }).pyramid;
      return (
        <>
          <h3>价值<em>金字塔</em></h3>
          {py?.corePromise && <div className="aim-ws-fact"><b>塔顶</b>{py.corePromise}</div>}
          {(py?.reasons ?? []).map((r, i) => (
            <div key={i} className="aim-ws-fact"><b>理由 {i + 1}</b>{r}</div>
          ))}
          {py?.evidence?.length ? <div className="aim-ws-fact"><b>证据层</b>{py.evidence.length} 条事实支撑</div> : null}
        </>
      );
    }
    case "b3": {
      const b3 = o as unknown as { acts?: Record<string, string>; actCores?: string[]; storyText?: string };
      const actLabels = ["世界观", "冲突", "英雄", "导师", "转变"];
      const actKeys = ["worldview", "conflict", "hero", "mentor", "transformation"];
      return (
        <>
          <h3>品牌<em>故事五幕</em></h3>
          {/* 048 F7：五幕逐幕全文呈现（不再 60 字截断） */}
          {actKeys.map((k, i) => b3.acts?.[k] ? (
            <div key={k} className="aim-ws-fact">
              <b>第 {i + 1} 幕 · {actLabels[i]}</b>
              {b3.actCores?.[i] ? `【${b3.actCores[i]}】` : ""}{b3.acts[k]}
            </div>
          ) : null)}
          {b3.storyText && <div className="aim-ws-fact"><b>连贯全文</b>{b3.storyText.slice(0, 500)}{b3.storyText.length > 500 ? "…" : ""}</div>}
        </>
      );
    }
    case "b4": {
      const b4 = o as unknown as { applicable?: boolean; simplified?: { conclusion?: string; revisitTrigger?: string }; decision?: { answers?: { q1?: string; q2?: string; q3?: string }; recommendedLabel?: string; namingRules?: string[]; riskReward?: { type?: string; text?: string }[] } };
      return (
        <>
          <h3>品牌<em>架构决策</em></h3>
          {b4.applicable === false ? (
            <>
              <div className="aim-ws-fact"><b>适用性结论</b>单品牌单业务，维持单一架构{b4.simplified?.conclusion ? ` — ${b4.simplified.conclusion}` : ""}</div>
              {b4.simplified?.revisitTrigger && <div className="aim-ws-fact"><b>重访触发</b>{b4.simplified.revisitTrigger}</div>}
            </>
          ) : (
            <>
              {b4.decision?.recommendedLabel && <div className="aim-ws-fact"><b>推荐架构</b>{b4.decision.recommendedLabel}</div>}
              {/* 048 F8：三问答案 + 风险收益完整呈现 */}
              {b4.decision?.answers?.q1 && <div className="aim-ws-fact"><b>三问决策树</b>Q1：{b4.decision.answers.q1}{b4.decision.answers.q2 ? `；Q2：${b4.decision.answers.q2}` : ""}{b4.decision.answers.q3 ? `；Q3：${b4.decision.answers.q3}` : ""}</div>}
              {(b4.decision?.namingRules ?? []).map((r, i) => (
                <div key={i} className="aim-ws-fact"><b>命名规则 {i + 1}</b>{r}</div>
              ))}
              {(b4.decision?.riskReward ?? []).map((r, i) => (
                <div key={i} className="aim-ws-fact"><b>{r.type === "risk" ? "风险" : "收益"} {i + 1}</b>{r.text}</div>
              ))}
            </>
          )}
        </>
      );
    }
    case "b5": {
      const b5 = o as unknown as { overview?: string; languageNorms?: { voiceHints?: string[]; forbiddenWords?: string[]; standardPromise?: string }; kpis?: { metric?: string; direction?: string; tracking?: string }[]; priorities?: { phaseLabel?: string; actions?: string[]; resource?: string }[] };
      return (
        <>
          <h3>策略<em>蓝皮书</em></h3>
          {b5.overview && <div className="aim-ws-fact"><b>B 线总览</b>{b5.overview}</div>}
          {b5.languageNorms?.standardPromise && <div className="aim-ws-fact"><b>标准承诺</b>{b5.languageNorms.standardPromise}</div>}
          {b5.languageNorms?.voiceHints?.length ? <div className="aim-ws-fact"><b>语气</b>{b5.languageNorms.voiceHints.join(" · ")}</div> : null}
          {b5.languageNorms?.forbiddenWords?.length ? <div className="aim-ws-fact"><b>禁用词</b>{b5.languageNorms.forbiddenWords.join("、")}</div> : null}
          {/* 048 F9：执行序列 + KPI 完整呈现 */}
          {(b5.priorities ?? []).map((p, i) => (
            <div key={i} className="aim-ws-fact"><b>{p.phaseLabel ?? `阶段 ${i + 1}`}</b>{(p.actions ?? []).join("；")}{p.resource ? `（资源：${p.resource}）` : ""}</div>
          ))}
          {(b5.kpis ?? []).map((k, i) => (
            <div key={i} className="aim-ws-fact"><b>KPI {i + 1}</b>{k.metric ?? "—"}{k.direction ? ` · ${k.direction}` : ""}{k.tracking ? ` · 追踪：${k.tracking}` : ""}</div>
          ))}
        </>
      );
    }
    // ---------- KBS S 线 ----------
    case "s1": {
      const s1 = o as unknown as { bigIdea?: { phrase?: string; tension?: string; directions?: string[] }; mediaTest?: { medium?: string; idea?: string }[] };
      return (
        <>
          <h3>Big Idea<em> 创意</em></h3>
          {s1.bigIdea?.phrase && <div className="aim-ws-fact"><b>核心表达</b>「{s1.bigIdea.phrase}」</div>}
          {s1.bigIdea?.tension && <div className="aim-ws-fact"><b>张力溯源</b>{s1.bigIdea.tension}</div>}
          {(s1.bigIdea?.directions ?? []).map((d, i) => (
            <div key={i} className="aim-ws-fact"><b>创意方向 {i + 1}</b>{d}</div>
          ))}
          {(s1.mediaTest ?? []).slice(0, 3).map((m, i) => (
            <div key={i} className="aim-ws-fact"><b>媒介测试 · {m.medium}</b>{m.idea}</div>
          ))}
        </>
      );
    }
    case "s2": {
      const s2 = o as unknown as { voice?: { trait?: string }[]; phases?: { name?: string; task?: string }[]; channels?: { name?: string }[] };
      return (
        <>
          <h3>传播<em>框架</em></h3>
          {s2.voice?.length ? <div className="aim-ws-fact"><b>声音调性</b>{s2.voice.map((v) => v.trait ?? "—").join(" · ")}</div> : null}
          {(s2.phases ?? []).map((p, i) => (
            <div key={i} className="aim-ws-fact"><b>{p.name ?? `阶段 ${i + 1}`}</b>{p.task ?? ""}</div>
          ))}
          {s2.channels?.length ? <div className="aim-ws-fact"><b>四渠道</b>{s2.channels.map((c) => c.name ?? "—").join(" · ")}</div> : null}
        </>
      );
    }
    case "s3": {
      const s3 = o as unknown as { rows?: { type?: string; share?: string }[]; cells?: { cost?: string }[]; priorityNote?: string };
      const high = (s3.cells ?? []).filter((c) => c.cost === "high").length;
      return (
        <>
          <h3>内容<em>矩阵</em></h3>
          {(s3.rows ?? []).map((r, i) => (
            <div key={i} className="aim-ws-fact"><b>{r.type ?? `第 ${i + 1} 行`}</b>占比 {r.share ?? "—"}</div>
          ))}
          <div className="aim-ws-fact"><b>高成本格</b>{s3.cells?.length ?? 16} 格中 {high} 个</div>
          {s3.priorityNote && <div className="aim-ws-fact"><b>优先级</b>{s3.priorityNote}</div>}
        </>
      );
    }
    case "s4": {
      const s4 = o as unknown as { singleTask?: string; audience?: string; desiredFeeling?: string; desiredAction?: string; successMetric?: string; mustAvoid?: string[] };
      const rows: [string, string | undefined][] = [
        ["单一任务", s4.singleTask], ["目标人群", s4.audience], ["期望感受", s4.desiredFeeling],
        ["期望行动", s4.desiredAction], ["成功指标", s4.successMetric],
      ];
      return (
        <>
          <h3>创意<em>简报</em></h3>
          {rows.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="aim-ws-fact"><b>{k}</b>{v}</div>
          ))}
          {s4.mustAvoid?.length ? <div className="aim-ws-fact"><b>必须避免</b>{s4.mustAvoid.join("、")}</div> : null}
        </>
      );
    }
    case "financial": {
      const roi = (o as Record<string, unknown>).roi;
      const rawForecast = (o as Record<string, unknown>).forecast;
      const forecast = Array.isArray(rawForecast) ? (rawForecast as unknown[]) : [];
      const roiStr = typeof roi === "object" && roi !== null
        ? String((roi as Record<string, unknown>).roiRatio ?? (roi as Record<string, unknown>).ratio ?? (roi as Record<string, unknown>).value ?? "—")
        : String(roi ?? "—");
      return (
        <>
          <h3>经营<em>测算</em></h3>
          <div className="aim-ws-kpi-row">
            <div><strong>{roiStr}</strong><span>ROI</span></div>
            <div><strong>{forecast.length}</strong><span>预测期数</span></div>
          </div>
          <p>完整测算表随 PPT「经营测算」页出片。</p>
        </>
      );
    }
    case "outline": {
      const detail = outlineDetail(step) ?? [];
      return (
        <>
          <h3>版式<em>节奏表</em></h3>
          {detail.map((line, i) => <div key={i} className="aim-ws-fact">{line}</div>)}
          {!detail.length && <p>大纲数据暂缺。</p>}
        </>
      );
    }
    case "drafting": {
      const rawSlides = (o as Record<string, unknown>).slides;
      const slides = Array.isArray(rawSlides) ? (rawSlides as { title?: string; subtitle?: string }[]) : [];
      return (
        <>
          <h3>逐页<em>文案</em>（前 4 页节选）</h3>
          {slides.slice(0, 4).map((s, i) => (
            <div key={i} className="aim-ws-fact"><b>第 {i + 1} 页</b>{s.title ?? "—"}{s.subtitle ? ` · ${s.subtitle}` : ""}</div>
          ))}
          {slides.length > 4 && <p>…共 {slides.length} 页，完整内容见 PPT。</p>}
        </>
      );
    }
    case "review": {
      const score = (o as Record<string, unknown>).score as number | undefined;
      const rawComments = (o as Record<string, unknown>).comments;
      const comments = Array.isArray(rawComments) ? rawComments : [];
      return (
        <>
          <h3>评审<em>报告</em></h3>
          <div className="aim-ws-kpi-row">
            <div><strong>{score ?? "—"}</strong><span>得分 / 100</span></div>
            <div><strong>{comments.length}</strong><span>条意见</span></div>
          </div>
          {comments.slice(0, 5).map((c, i) => <div key={i} className="aim-ws-fact">{commentText(c)}</div>)}
        </>
      );
    }
    case "artwork": {
      // 048 F3：配图降级告知（失败/跳过时明说，并指引去结果页重试）
      // 049 F10：失败原因按分类如实展示（不再笼统写「服务暂时不可用」）
      const st = (o as unknown as { stats?: { total?: number; done?: number; failed?: number; skipped?: string; perImage?: { ok: boolean; error?: string }[] } }).stats;
      const failed = st?.failed ?? 0;
      const lastErr = st?.perImage?.filter((p) => !p.ok && p.error).slice(-1)[0]?.error;
      const reason = artworkFailReason(lastErr);
      return (
        <>
          <h3>AI <em>配图</em></h3>
          {st?.skipped ? (
            <p>本次未启用配图生成（{st.skipped === "off" ? "开关已关闭" : "服务未配置"}），图文页保留占位框。</p>
          ) : (
            <>
              <div className="aim-ws-kpi-row">
                <div><strong>{st?.done ?? 0}</strong><span>已生成</span></div>
                <div><strong>{failed}</strong><span>失败</span></div>
                <div><strong>{st?.total ?? 0}</strong><span>目标张数</span></div>
              </div>
              {failed > 0
                ? <p>⚠ {failed} 张配图生成失败（{reason}），对应页面保留占位框。可到方案结果页点「重新生成配图」补出。</p>
                : (st?.done ?? 0) > 0 ? <p>✅ 配图已全部生成并注入图文页。</p> : <p>未发现需要配图的目标页。</p>}
            </>
          )}
        </>
      );
    }
    case "assemble":
      return (
        <>
          <h3>PPT <em>已出片</em></h3>
          <p>整册已装配完成，进入编辑器可逐页微调、换主题、导出 .pptx。</p>
          {deckId && (
            <button type="button" className="aim-ws-go-editor" onClick={() => nav(`/deck/${deckId}/edit`)}>
              打开编辑器 ↗
            </button>
          )}
        </>
      );
    default:
      return <p>暂无可预览内容。</p>;
  }
}

export default function Workspace() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState<JobInfo | null>(null);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [project, setProject] = useState<{ name: string; industry: string | null; form: Record<string, unknown> } | null>(null);
  const [deckId, setDeckId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [messages, setMessages] = useState<WsMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const streamRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  /** done 后自动跳转的 timer（045 #14：离开页面时必须 clearTimeout，防被拽回） */
  const navTimer = useRef<number | null>(null);
  /** 本页是否见过「进行中」状态——见过才允许 done 后自动跳结果页（回看不跳） */
  const sawActiveRef = useRef(false);

  async function refresh() {
    if (!jobId) return;
    try {
      // 045 #13：成功轮询即清错误横幅（原只在 catch 里 set，瞬时失败后永不清除）
      setErr(null);
      const info = await api.getJob(jobId);
      setJob(info);
      // 只有「本页眼睁睁看着它跑完」（进行中→done 的转场）才自动跳结果页；
      // 显式回看已完成任务时停留在过程页（铨叔实测：点「生成过程」被弹走）
      if (info.status !== "done" && info.status !== "failed" && info.status !== "canceled") {
        sawActiveRef.current = true;
      }
      if (info.status === "done" && sawActiveRef.current && !deckId) {
        const { data } = await supabase.from("decks").select("id")
          .eq("job_id", jobId).limit(1).maybeSingle();
        if (data?.id) {
          setDeckId(data.id);
          navTimer.current = window.setTimeout(() => nav(`/deck/${data.id}`, { replace: true }), 1500);
        }
      } else if (info.status === "done" && !deckId) {
        // 回看场景：静默取 deckId 供「查看结果」按钮用，不跳转
        const { data } = await supabase.from("decks").select("id")
          .eq("job_id", jobId).limit(1).maybeSingle();
        if (data?.id) setDeckId(data.id);
      }
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  // 项目列表（栏 1）+ 当前项目资料（栏 2）
  useEffect(() => {
    api.listProjects().then((r) => setProjects(r.projects)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!jobId) return;
    (async () => {
      const { data: j } = await supabase.from("jobs").select("project_id").eq("id", jobId).maybeSingle();
      if (!j?.project_id) return;
      const { data: p } = await supabase.from("projects")
        .select("name, industry, form").eq("id", j.project_id).maybeSingle();
      if (p) setProject({ name: p.name, industry: p.industry, form: (p.form ?? {}) as Record<string, unknown> });
    })();
  }, [jobId]);

  // 027 P2：job_messages 初始全量 + Realtime INSERT 增量（刷新不丢）
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.from("job_messages").select("*").eq("job_id", jobId)
          .order("seq", { ascending: true });
        if (!cancelled && data) setMessages(data as WsMessage[]);
      } catch { /* 云端迁移未生效等场景降级为 P1 合成叙事，不抛未处理异常 */ }
    })();
    const ch = supabase.channel(`msgs:${jobId}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "job_messages", filter: `job_id=eq.${jobId}` },
        (payload) => {
          const row = payload.new as WsMessage;
          setMessages((prev) =>
            prev.some((x) => x.id === row.id) ? prev : [...prev, row].sort((a, b) => a.seq - b.seq));
        })
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, [jobId]);

  // 轮询 + Realtime 兜底（沿用旧 Job 页模式）
  useEffect(() => {
    refresh();
    timer.current = window.setInterval(refresh, 3000);
    const channel = supabase.channel(`job:${jobId}`)
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "jobs", filter: `id=eq.${jobId}` },
        () => refresh())
      .subscribe();
    return () => {
      if (timer.current) window.clearInterval(timer.current);
      // 045 #14：done 后 1.5s 内离开时取消自动跳转
      if (navTimer.current) window.clearTimeout(navTimer.current);
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // 新消息到达时滚动到底（仅当用户贴近底部时，避免打断回看）
  useEffect(() => {
    const el = streamRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 160) el.scrollTop = el.scrollHeight;
  }, [job?.steps.length, job?.status, job?.progress, messages.length]);

  const status = job?.status ?? "queued";
  const terminal = ["done", "failed", "canceled"].includes(status);
  const paused = !!job?.paused && !terminal;
  const stages = stagesFor(job?.pipeline ?? "kbs");
  // job_steps 一步一行只增不改：同一步骤重试会产生多行，展示一律取最新行（032 产物 KV 约定）
  const stepLatest = new Map<string, StepInfo>();
  for (const s of job?.steps ?? []) stepLatest.set(s.step, s);
  const runningStep = stages.find((s) => stepLatest.get(s.key)?.status === "running");
  const doneSteps = (job?.steps ?? []).filter((s) => s.status === "done");
  const viewingKey = viewing ?? (doneSteps.length ? doneSteps[doneSteps.length - 1].step : runningStep?.key ?? null);
  const viewingStep = viewingKey ? stepLatest.get(viewingKey) ?? null : null;
  const costYuan = ((job?.cost_cents ?? 0) / 100).toFixed(2);

  const chip = status === "done" ? "已完成 · 即将进入方案"
    : status === "failed" ? "生成失败"
    : status === "canceled" ? "已取消"
    : status === "queued" ? "排队中"
    : paused ? "已暂停 · 等您的意见"
    : `生成中 · ${runningStep ? runningStep.label : `${job?.progress ?? 0}%`}`;
  const chipCls = `aim-ws-status-chip${status === "failed" ? " is-err" : status === "canceled" || paused ? " is-off" : ""}`;

  function goProject(p: ProjectListItem) {
    const latest = [...p.jobs].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
    if (latest) nav(`/job/${latest.id}`);
    else if (p.decks.length) nav(`/deck/${p.decks[0].id}`);
    else nav("/projects");
  }

  return (
    <div className="aim-ws">
      {/* 导航栏 */}
      <header className="aim-ws-topbar">
        <Link className="aim-ws-wordmark" to="/projects" aria-label="策略咖 · 我的方案">
          <BrandLogo />
        </Link>
        <div className="aim-ws-topbar-title">
          <p className="aim-ws-eyebrow">LIVE WORKSPACE / 生成工作台</p>
          <span className="aim-ws-pname">{project?.name ?? "加载中…"}</span>
          <span className={chipCls}>{chip}</span>
        </div>
        <div className="aim-ws-topbar-actions">
          <span className="aim-ws-mail">{user?.email ?? ""}</span>
          {status === "failed" && (
            <button type="button" className="aim-ws-ghost" onClick={async () => { await api.retryJob(jobId!); refresh(); }}>
              🔁 重试
            </button>
          )}
          {!terminal && (
            <button type="button" className="aim-ws-ghost" onClick={async () => {
              if (window.confirm("确定取消本次生成任务？已完成的步骤产物会保留。")) {
                await api.cancelJob(jobId!); refresh();
              }
            }}>取消任务</button>
          )}
          <button type="button" className="aim-ws-ghost" onClick={() => nav("/projects")}>我的方案 ↗</button>
        </div>
      </header>

      <div className="aim-ws-grid">
        {/* 栏 1 · 历史项目 */}
        <aside className="aim-ws-col" aria-label="历史项目">
          <div className="aim-ws-col-head"><p className="aim-ws-eyebrow">PROJECTS</p><h2>历史项目</h2></div>
          <div className="aim-ws-col-body">
            {projects.map((p) => {
              const latest = [...p.jobs].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
              const active = !!latest && latest.id === jobId;
              const sub = latest
                ? `${latest.status === "done" ? "已出片" : latest.status === "failed" ? "失败" : latest.status === "canceled" ? "已取消" : "生成中"} · ${new Date(p.created_at).toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" })}`
                : p.decks.length ? "已出片" : "待生成";
              return (
                <button key={p.id} type="button" className={`aim-ws-proj${active ? " is-active" : ""}`} onClick={() => goProject(p)}>
                  <b>{p.name}</b><span>{sub}</span>
                </button>
              );
            })}
            {!projects.length && <p style={{ fontSize: 12, color: "var(--ws-muted)" }}>暂无项目</p>}
          </div>
        </aside>

        {/* 栏 2 · 品牌资料 */}
        <aside className="aim-ws-col" aria-label="品牌资料">
          <div className="aim-ws-col-head"><p className="aim-ws-eyebrow">BRIEF / 输入资料</p><h2>品牌资料</h2></div>
          <div className="aim-ws-col-body aim-ws-brief">
            <dl>
              <div><dt>品牌</dt><dd>{project?.name ?? "—"}</dd></div>
              {project?.industry && <div><dt>行业</dt><dd>{project.industry}</dd></div>}
              {Object.entries(project?.form ?? {}).map(([k, v]) => (
                FORM_LABELS[k] && v != null && v !== "" ? (
                  <div key={k}><dt>{FORM_LABELS[k]}</dt><dd>{String(v)}</dd></div>
                ) : null
              ))}
            </dl>
            {(() => {
              /* 北极星常驻块：k3 完成后展示战略主线，跨步骤可见（034 §1.2 三级读取） */
              const k3 = stepLatest.get("k3");
              const ns = k3?.status === "done"
                ? (k3.output as { northStar?: { sentence?: string } } | null)?.northStar?.sentence
                : undefined;
              return ns ? (
                <div className="aim-ws-northstar">
                  <p className="aim-ws-eyebrow">NORTH STAR / 北极星语句</p>
                  <p>{ns}</p>
                </div>
              ) : null;
            })()}
            <p className="aim-ws-brief-note">可随时「⏸ 暂停」任务并在输入框留下修改意见，Agent 下一步即采纳（P3 已上线）。</p>
          </div>
        </aside>

        {/* 栏 3 · Agent 工作区 */}
        <section className="aim-ws-col aim-ws-col-stream" aria-label="Agent 工作区">
          <div className="aim-ws-stream-head">
            <div>
              <p className="aim-ws-eyebrow">AGENT STREAM / 思考与创作实时流</p>
              <h2>把黑盒打开，<em>边看边改。</em></h2>
            </div>
            <div className="aim-ws-stream-head-right">
              <span className="aim-ws-progress-mini">{job?.progress ?? 0}%</span>
              {paused ? (
                <button type="button" className="aim-ws-pause-btn" onClick={async () => { await api.resumeJob(jobId!); refresh(); }}>▶ 继续</button>
              ) : (
                <button type="button" className="aim-ws-pause-btn" disabled={terminal} onClick={async () => { await api.pauseJob(jobId!); refresh(); }}>⏸ 暂停</button>
              )}
            </div>
          </div>

          <div className={`aim-ws-stream${paused ? " is-paused" : ""}`} ref={streamRef} aria-live="polite">
            {messages.length > 0 ? (
              /* ---- P2：job_messages 真实消息流 ---- */
              messages.map((m, i) => {
                const prev = i > 0 ? messages[i - 1] : null;
                const stageIdx = stages.findIndex((s) => s.key === m.stage);
                const stage = stageIdx >= 0 ? stages[stageIdx] : null;
                const showDivider = !!m.stage && m.stage !== prev?.stage;
                const artStep = m.artifact_ref ? stepLatest.get(m.artifact_ref) : undefined;
                const isLast = i === messages.length - 1;
                const live = !terminal && isLast && m.role === "agent" && !m.artifact_ref;
                return (
                  <div key={m.id} style={{ display: "contents" }}>
                    {showDivider && (
                      <p className="aim-ws-stage-divider">
                        <b>{stageIdx >= 0 ? String(stageIdx + 1).padStart(2, "0") : "··"}</b> {stage?.divider ?? m.stage}
                      </p>
                    )}
                    {m.role === "user" ? (
                      <article className="aim-ws-msg is-user">
                        <div className="aim-ws-who"><span className="aim-ws-tag">您 · 修改意见</span><time>{fmtTime(m.created_at)}</time></div>
                        <p className="aim-ws-say">{m.say}</p>
                      </article>
                    ) : m.role === "system" ? (
                      <article className="aim-ws-msg is-err">
                        <div className="aim-ws-who"><span className="aim-ws-tag">系统</span><time>{fmtTime(m.created_at)}</time></div>
                        <p className="aim-ws-say">{m.say}</p>
                      </article>
                    ) : (
                      <article className="aim-ws-msg">
                        <div className="aim-ws-who"><span className="aim-ws-tag">{m.agent_name ?? "AGENT"}</span><time>{fmtTime(m.created_at)}</time></div>
                        {m.think && <p className="aim-ws-think">{m.think}</p>}
                        <p className="aim-ws-say">
                          {m.say}
                          {live && <span className="aim-ws-caret" aria-hidden="true" />}
                        </p>
                      </article>
                    )}
                    {m.artifact_ref && stage && artStep?.status === "done" && (
                      <article
                        className={`aim-ws-msg is-artifact${viewingKey === m.artifact_ref ? " is-viewing" : ""}`}
                        onClick={() => setViewing(m.artifact_ref)}
                      >
                        <div className="aim-ws-art-l">
                          <b>{stage.icon} {stage.label}</b>
                          <span>{summarize(artStep) ?? "查看产物"}</span>
                        </div>
                        <span className="aim-ws-art-r" aria-hidden="true">↗</span>
                      </article>
                    )}
                  </div>
                );
              })
            ) : (
              /* ---- 回退：历史任务无消息时用步骤数据合成叙事（P1 版） ---- */
              stages.map((stage, idx) => {
                const step = stepLatest.get(stage.key);
                if (!step || (step.status === "pending" && !step.started_at)) return null;
                const isDone = step.status === "done";
                const isRunning = step.status === "running";
                const isFailed = step.status === "failed";
                const summary = summarize(step);
                return (
                  <div key={stage.key} style={{ display: "contents" }}>
                    <p className="aim-ws-stage-divider"><b>{String(idx + 1).padStart(2, "0")}</b> {stage.divider}</p>
                    <article className="aim-ws-msg">
                      <div className="aim-ws-who">
                        <span className="aim-ws-tag">{stage.label} AGENT</span>
                        <time>{fmtTime(step.started_at)}</time>
                      </div>
                      {summary && isDone && <p className="aim-ws-think">{summary}</p>}
                      <p className="aim-ws-say">
                        {isDone ? `${stage.label}完成。` : isFailed ? `${stage.label}遇到问题，已停止。` : `正在${stage.divider.split(" · ")[1] ?? stage.label}…`}
                        {isRunning && <span className="aim-ws-caret" aria-hidden="true" />}
                      </p>
                    </article>
                    {step.error && (
                      <article className="aim-ws-msg is-err">
                        <div className="aim-ws-who"><span className="aim-ws-tag">错误</span></div>
                        <p className="aim-ws-say">{step.error.slice(0, 160)}</p>
                      </article>
                    )}
                    {isDone && (
                      <article
                        className={`aim-ws-msg is-artifact${viewingKey === stage.key ? " is-viewing" : ""}`}
                        onClick={() => setViewing(stage.key)}
                      >
                        <div className="aim-ws-art-l">
                          <b>{stage.icon} {stage.label}</b>
                          <span>{summary ?? "查看产物"}</span>
                        </div>
                        <span className="aim-ws-art-r" aria-hidden="true">↗</span>
                      </article>
                    )}
                  </div>
                );
              })
            )}
            {job?.error && (
              <article className="aim-ws-msg is-err">
                <div className="aim-ws-who"><span className="aim-ws-tag">任务错误</span></div>
                <p className="aim-ws-say">{job.error}</p>
              </article>
            )}
            {err && (
              <article className="aim-ws-msg is-err">
                <div className="aim-ws-who"><span className="aim-ws-tag">网络</span></div>
                <p className="aim-ws-say">{err}</p>
              </article>
            )}
            {status === "done" && (
              <p className="aim-ws-msg is-sys">✅ 全部完成，正在进入方案页…</p>
            )}
            {!job && !err && <p className="aim-ws-msg is-sys">正在载入任务…</p>}
          </div>

          <form className="aim-ws-composer" onSubmit={async (e) => {
            e.preventDefault();
            const text = draft.trim();
            if (!text || !jobId || sending) return;
            setSending(true);
            try {
              await api.sendFeedback(jobId, text);
              setDraft("");
            } catch (e2) {
              setErr((e2 as Error).message);
            }
            setSending(false);
          }}>
            <input
              type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
              placeholder={terminal ? "任务已结束" : "留下修改意见，Agent 下一步会采纳…"}
              autoComplete="off" disabled={terminal || sending}
            />
            <button type="submit" className="aim-ws-send-btn" disabled={terminal || sending || !draft.trim()} title="发送修改意见" aria-label="发送修改意见">↗</button>
            {/* 048 F2：icon-only 改醒目文字按钮，生成中随时可见可点 */}
            <button
              type="button" className={`aim-ws-stop-btn${paused ? " is-paused" : ""}`} disabled={terminal}
              title={paused ? "继续生成" : "暂停生成"}
              onClick={async () => {
                if (paused) await api.resumeJob(jobId!); else await api.pauseJob(jobId!);
                refresh();
              }}
            >{paused ? "▶ 继续" : "⏸ 暂停"}</button>
          </form>
          <p className="aim-ws-composer-hint">
            {(job?.llm_calls ?? 0) > 0 ? `🧠 ${job!.llm_calls} 次 AI 调用 · 📝 ${((job!.tokens_total ?? 0) / 1000).toFixed(1)}k tokens · 💰 ¥${costYuan} ｜ ` : ""}
            {messages.length > 0
              ? paused
                ? "已暂停：留下修改意见后点「继续」，Agent 下一步即采纳。"
                : "P3：可随时暂停并留下意见，Agent 下一步会采纳；不打断则一路出片。"
              : "历史任务：叙事由步骤真实数据回放；新生成的任务支持暂停与意见注入。"}
          </p>
        </section>

        {/* 栏 4 · 产物预览 */}
        <aside className="aim-ws-col" aria-label="阶段产物预览">
          <div className="aim-ws-col-head"><p className="aim-ws-eyebrow">ARTIFACTS / 阶段产物</p><h2>点开即预览</h2></div>
          <div className="aim-ws-col-body">
            <div className="aim-ws-art-list">
              {stages.map((stage, idx) => {
                const step = stepLatest.get(stage.key);
                const st = step?.status ?? "pending";
                const cls = st === "done" ? "is-done" : st === "running" ? "is-live" : "is-locked";
                const stLabel = st === "done" ? "✓ 完成" : st === "running" ? "● 进行中" : st === "failed" ? "✕ 失败" : "—";
                const clickable = st === "done" || st === "running";
                return (
                  <button
                    key={stage.key}
                    type="button"
                    className={`aim-ws-art-item ${cls}${viewingKey === stage.key && clickable ? " is-viewing" : ""}`}
                    disabled={!clickable}
                    onClick={() => clickable && setViewing(stage.key)}
                  >
                    <span className="aim-ws-no">{String(idx + 1).padStart(2, "0")}</span>
                    {stage.label}
                    <span className="aim-ws-st">{stLabel}</span>
                  </button>
                );
              })}
            </div>
            <div className="aim-ws-preview">
              <div className="aim-ws-preview-head">
                <b>{viewingStep ? `${stages.find((s) => s.key === viewingStep.step)?.label ?? viewingStep.step} · ${viewingStep.status === "done" ? "产物" : "进行中"}` : "阶段产物预览"}</b>
                <span>{viewingStep?.status === "running" ? "LIVE" : "PREVIEW"}</span>
              </div>
              <div className="aim-ws-preview-body">
                {viewingStep && viewingStep.output
                  ? <WsErrorBoundary><PreviewBody step={viewingStep} deckId={deckId} /></WsErrorBoundary>
                  : <p style={{ color: "var(--ws-muted)" }}>暂无可预览内容——完成一个步骤后，点这里查看该阶段产物。</p>}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
